import type {
    TableOfContentsEntry,
    ValidationQuestionDefinition,
    ValidationResultItem,
} from 'ComponentLibrary/src/playground/tools/PdfValidator/types'

export type PdfValidatorWorkflowLocalizedText = {
    de: string
    en: string
}

export type PdfValidatorWorkflowWarning = {
    warning_type: string
    message: PdfValidatorWorkflowLocalizedText
}

export type PdfValidatorWorkflowStepStatus = 'pending' | 'running' | 'retrying' | 'completed' | 'not_found' | 'failed' | 'skipped'

export type PdfValidatorWorkflowProgressStep = {
    id: string
    label: PdfValidatorWorkflowLocalizedText
    description: PdfValidatorWorkflowLocalizedText
    status: PdfValidatorWorkflowStepStatus
    completed_units?: number
    total_units?: number
    unit?: string
}

export type PdfValidatorWorkflowPartialResult = {
    step_id: string
    revision: number
    value: unknown
}

export type PdfValidatorWorkflowProgressSnapshot = {
    version: 1
    sequence: number
    steps: PdfValidatorWorkflowProgressStep[]
    partial_results: PdfValidatorWorkflowPartialResult[]
    warnings: PdfValidatorWorkflowWarning[]
}

export type PdfValidatorWorkflowPdfFile = {
    name: string
    source: {
        sourceType: 'BYTE_CONTENT'
        byteContent: {
            mediaType: 'application/pdf'
            data: string
        }
    }
}

export type PdfValidatorWorkflowInvoker = (invocation: {
    prompt: string
    files: [PdfValidatorWorkflowPdfFile]
    onProgress?: (progress: PdfValidatorWorkflowProgressSnapshot) => void
    onPartialResult?: (partialResult: PdfValidatorWorkflowPartialResult) => void
}) => Promise<{ response: string }>

export type PdfValidatorWorkflowResultItem = {
    id: string
    status: 'yes' | 'no' | 'unknown' | 'text_answer'
    answer: string | null
    source_text: string | null
    page: number | null
}

export type PdfValidatorAnalyzeWorkflowResult = {
    table_of_contents: TableOfContentsEntry[]
    results: PdfValidatorWorkflowResultItem[]
    warnings: PdfValidatorWorkflowWarning[]
}

export type PdfValidatorAnswerWorkflowResult = {
    results: PdfValidatorWorkflowResultItem[]
    warnings: PdfValidatorWorkflowWarning[]
}

const buildWorkflowCommand = (operation: 'analyze' | 'answer', questions: ValidationQuestionDefinition[]) =>
    `einwert-ai-workflow:v1\n${JSON.stringify({ workflow: 'plausibility', operation, input: { questions } })}`

export const buildPlausibilityAnalyzeCommand = (questions: ValidationQuestionDefinition[]) => buildWorkflowCommand('analyze', questions)

export const buildPlausibilityAnswerCommand = (questions: ValidationQuestionDefinition[]) => buildWorkflowCommand('answer', questions)

export const toValidationResultItems = (results: PdfValidatorWorkflowResultItem[]): ValidationResultItem[] =>
    results.map(({ id, status, answer, source_text, page }) => ({
        id,
        status,
        answer,
        sourceText: source_text,
        page,
    }))
